import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  Suspense,
  lazy,
} from "react";
import { Modal, Button, Spin } from "antd";
import { getMsg } from "../utils/antdStatic";
import {
  CameraOutlined,
  EditOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { compressImage } from "../utils/imageCompression";

// Lazy-load del editor — round 2026-04-29 bundle optimization. Antes
// FilerobotImageEditor + konva + react-konva (~570 KB) se importaban
// estáticamente, inflando el main bundle aunque nadie abriera el modal.
// React.lazy + Suspense crea un chunk aparte que solo se descarga cuando
// el usuario llega al paso 2 (editar). El paso 1 (selector de imagen)
// no necesita filerobot, así que el chunk no se carga en ese path.
const FilerobotEditor = lazy(() => import("./FilerobotEditor"));

interface ImageEditorModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (compressedDataUrl: string) => Promise<void>;
  saving?: boolean;
  /** Pre-load an image (data URL) so the editor opens directly, skipping the file picker. */
  initialImage?: string | null;
  /** Modo del editor Filerobot. `"crop"` abre directo en Recortar (ej. avatar). */
  editorMode?: "annotate" | "crop";
}

const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  open,
  onClose,
  onSave,
  saving: externalSaving,
  initialImage,
  editorMode,
}) => {
  const [saving, setSaving] = useState(false);
  // Inicializar desde `initialImage` evita el parpadeo del paso 1 ("Agregar
  // imagen") cuando se abre el editor directo con una imagen ya elegida
  // (ej. avatar). Para forzar re-montaje con imagen nueva, el llamador pasa
  // un `key` distinto.
  const [imageSource, setImageSource] = useState<string | null>(
    initialImage ?? null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Input del path directo ("Subir sin editar"): salta Filerobot por
  // completo para evitar el re-encoding JPEG extra (aunque sea a
  // quality 0.98) que introduce pérdida perceptible sin agregar nada
  // cuando no hay edición. Ver config-optimizacion-imagenes.md §2.1.
  const directInputRef = useRef<HTMLInputElement>(null);
  const isSaving = externalSaving || saving;

  // If an initialImage is provided, jump straight to the editor
  useEffect(() => {
    if (open && initialImage) setImageSource(initialImage);
  }, [open, initialImage]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageSource(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  /** Path directo: sin Filerobot, solo compressImage. El dataUrl del
   *  File va directo al pipeline de compresión (mismo que usa el
   *  preview admin). Preserva máxima calidad cuando no hay edición. */
  const handleDirectUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (isSaving) return;
    if (!file.type.startsWith("image/")) {
      getMsg()?.error("Selecciona un archivo de imagen");
      return;
    }
    try {
      setSaving(true);
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const compressed = await compressImage(dataUrl);
      if (!compressed) {
        getMsg()?.error("No se pudo procesar la imagen.");
        return;
      }
      await onSave(compressed);
      setImageSource(null);
    } catch (err: any) {
      getMsg()?.error(err?.message || "Error subiendo imagen");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setImageSource(null);
    onClose();
  };

  const handleSave = useCallback(
    async (savedImageData: any) => {
      if (isSaving) return;

      const base64 = savedImageData?.imageBase64;
      if (!base64) {
        getMsg()?.error("No se pudo obtener la imagen editada.");
        return;
      }

      const dataUrl = base64.startsWith("data:")
        ? base64
        : `data:image/jpeg;base64,${base64}`;

      try {
        setSaving(true);
        const compressed = await compressImage(dataUrl);
        if (!compressed) {
          getMsg()?.error("No se pudo procesar la imagen.");
          return;
        }
        await onSave(compressed);
        setImageSource(null);
      } catch (e: any) {
        getMsg()?.error(e?.message || "Error guardando imagen");
      } finally {
        setSaving(false);
      }
    },
    [isSaving, onSave],
  );

  if (!open) return null;

  // Paso 1: seleccionar imagen
  if (!imageSource) {
    return (
      <Modal
        title="Agregar imagen"
        open={open}
        onCancel={handleClose}
        footer={null}
        width={460}
        destroyOnClose
        maskClosable={!isSaving}
        keyboard={!isSaving}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        <input
          ref={directInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleDirectUpload}
        />
        <input
          id="camera-input"
          type="file"
          accept="image/*,video/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: "16px 0 4px",
          }}
        >
          <Button
            block
            type="primary"
            size="large"
            icon={<SendOutlined />}
            loading={isSaving}
            onClick={() => directInputRef.current?.click()}
            style={{ height: 52 }}
          >
            Subir directo
          </Button>
          <div style={{ fontSize: 12, color: "#666", marginTop: -6 }}>
            Más rápido. Solo imágenes. La compresión aplica la config de
            /administradores → Optimización de imágenes.
          </div>

          <Button
            block
            size="large"
            icon={<EditOutlined />}
            disabled={isSaving}
            onClick={() => fileInputRef.current?.click()}
            style={{ height: 48, marginTop: 12 }}
          >
            Editar antes de subir
          </Button>
          <div style={{ fontSize: 12, color: "#666", marginTop: -6 }}>
            Abre el editor para recortar, rotar o agregar texto/flechas.
            Misma calidad final que "Subir directo".
          </div>

          <Button
            block
            size="large"
            icon={<CameraOutlined />}
            disabled={isSaving}
            onClick={() => document.getElementById("camera-input")?.click()}
            style={{ height: 48, marginTop: 12 }}
          >
            Tomar foto / video
          </Button>
        </div>
      </Modal>
    );
  }

  // Paso 2: editor con la imagen seleccionada
  return (
    <Modal
      title="Editor de imagen"
      open={open}
      onCancel={handleClose}
      footer={null}
      width="calc(100vw - 48px)"
      style={{ maxWidth: 1200, top: 20 }}
      styles={{ body: { height: "80vh", padding: 0, overflow: "hidden" } }}
      destroyOnClose
      maskClosable={!isSaving}
      keyboard={!isSaving}
    >
      <div style={{ width: "100%", height: "100%" }}>
        <Suspense
          fallback={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Spin size="large" tip="Cargando editor..." />
            </div>
          }
        >
          <FilerobotEditor
            imageSource={imageSource}
            onSave={handleSave}
            onClose={handleClose}
            editorMode={editorMode}
          />
        </Suspense>
      </div>
    </Modal>
  );
};

export default ImageEditorModal;
